import { motion } from "framer-motion";
import {
  Monitor,
  SlidersHorizontal,
  LayoutGrid,
  Presentation,
  Gauge,
  Users,
  Armchair,
  Frame,
  Tv,
  Archive,
  TrendingUp,
  Wrench,
  Warehouse,
  Building2,
  PackageOpen,
  ClipboardCheck,
  Boxes,
  Ruler,
  Puzzle,
  ArrowUpRight,
} from "lucide-react";
import { mailto, scrollToId } from "./nav";

const SUBCATEGORIES = [
  {
    icon: Monitor,
    title: "Диспетчерские столы",
    desc: "Специализированные рабочие столы для диспетчеров, операторов и сотрудников центров управления.",
  },
  {
    icon: SlidersHorizontal,
    title: "Диспетчерские пульты",
    desc: "Рабочие консоли и пульты для размещения оборудования, мониторов и средств управления.",
  },
  {
    icon: LayoutGrid,
    title: "Мебель для центров управления",
    desc: "Модульные решения для центров управления, операторных и рабочих помещений с длительным режимом эксплуатации.",
  },
  {
    icon: Presentation,
    title: "Столы для ситуационных центров",
    desc: "Рабочие места и модульные столы для ситуационных, аналитических и координационных центров.",
  },
  {
    icon: Gauge,
    title: "Пульты и рабочие места операторов",
    desc: "Профессиональные рабочие места для операторов промышленных и телекоммуникационных систем.",
  },
  {
    icon: Users,
    title: "Столы для переговорных и селекторных совещаний",
    desc: "Функциональные столы и рабочие зоны для переговорных комнат и селекторных совещаний.",
  },
  {
    icon: Armchair,
    title: "Промышленные и диспетчерские кресла",
    desc: "Эргономичные кресла для продолжительной работы операторов и диспетчеров.",
  },
  {
    icon: Frame,
    title: "Кронштейны для мониторов",
    desc: "Настольные, настенные и регулируемые крепления для одного или нескольких мониторов.",
  },
  {
    icon: Tv,
    title: "Крепления для видеостен",
    desc: "Крепления и несущие конструкции для монтажа видеостен и информационных панелей.",
  },
  {
    icon: Archive,
    title: "Металлическая мебель, корпуса и тумбы",
    desc: "Металлическая мебель, шкафы, корпуса и тумбы для размещения оборудования и организации рабочих зон.",
  },
  {
    icon: TrendingUp,
    title: "Столы для трейдеров и аналитиков",
    desc: "Многомониторные рабочие места для финансовых, аналитических и диспетчерских центров.",
  },
  {
    icon: Wrench,
    title: "Фурнитура и комплектующие",
    desc: "Кабель-каналы, опоры, каркасы, механизмы регулировки высоты, крепёж, держатели и другие комплектующие для промышленной мебели.",
  },
  {
    icon: Warehouse,
    title: "Мебель для складских помещений",
    desc: "Складская и коммерческая мебель: рабочие столы, верстаки, комплектация мебелью производственных и складских зон.",
  },
  {
    icon: Building2,
    title: "Офисная мебель для промышленных объектов",
    desc: "Офисная мебель для административных помещений на промышленных площадках и производственных предприятиях.",
  },
  {
    icon: PackageOpen,
    title: "Промышленные системы хранения",
    desc: "Стеллажи, шкафы, ящики и модульные системы промышленного хранения для складов, цехов и участков.",
  },
];

const FEATURES = [
  {
    icon: ClipboardCheck,
    title: "Подбор под техническое задание",
    desc: "Подбираем позиции по вашему ТЗ с учётом назначения помещения и требований к оборудованию.",
  },
  {
    icon: Boxes,
    title: "Модульные решения",
    desc: "Комплектуем модульные рабочие места и мебельные линии для операторных и центров управления.",
  },
  {
    icon: Ruler,
    title: "Эргономичная организация",
    desc: "Предлагаем решения для эргономичной организации рабочих мест и продолжительной работы операторов.",
  },
  {
    icon: Puzzle,
    title: "Комплектующие и фурнитура",
    desc: "Поставляем крепёж, кабель-каналы, каркасы, механизмы регулировки и другие элементы для промышленной мебели.",
  },
];

export default function Furniture() {
  return (
    <section
      id="furniture"
      data-testid="furniture-section"
      className="section bg-white relative"
    >
      <div className="container-x">
        {/* Header row with image */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center mb-16 lg:mb-24">
          <div className="lg:col-span-6">
            <div className="eyebrow mb-6">Ключевое направление</div>
            <h2 className="font-display text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1] tracking-tight break-words">
              Промышленная мебель и&nbsp;фурнитура
            </h2>
            <p className="mt-6 text-slate-600 text-base lg:text-lg leading-relaxed">
              Одно из ключевых направлений B2B-поставок: промышленная, коммерческая, складская
              и металлическая мебель, офисная мебель для промышленных объектов, промышленные
              системы хранения, а также фурнитура и комплектующие для мебели.
            </p>
            <p className="mt-4 text-slate-600 text-base lg:text-lg leading-relaxed">
              Работаем как оптовый поставщик: подбираем номенклатуру под задачи предприятия,
              комплектуем модульные решения для диспетчерских, операторных, ситуационных центров
              и промышленных объектов, поставляем сопутствующую фурнитуру.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              {[
                "Промышленная",
                "Коммерческая",
                "Складская",
                "Металлическая",
                "Офисная",
                "Системы хранения",
                "Фурнитура",
              ].map((t) => (
                <span
                  key={t}
                  className="text-xs uppercase tracking-[0.14em] font-medium text-slate-500 border border-slate-200 px-3 py-1.5"
                >
                  {t}
                </span>
              ))}
            </div>

            <a
              href={mailto("Запрос: промышленная мебель и фурнитура")}
              data-testid="furniture-main-cta"
              className="mt-10 inline-flex items-center gap-2 bg-[#FF6B00] hover:bg-[#E66000] text-white px-8 py-4 font-medium transition-colors"
            >
              Запросить предложение
              <ArrowUpRight className="w-4 h-4" />
            </a>
          </div>

          <div className="lg:col-span-6">
            <div className="relative overflow-hidden">
              <img
                src="https://images.pexels.com/photos/3862132/pexels-photo-3862132.jpeg"
                alt="Рабочее место оператора центра управления"
                className="w-full h-[380px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/40 via-transparent to-transparent" />
              <div className="absolute top-6 left-6 glass-card-dark px-4 py-2 text-white text-xs uppercase tracking-[0.2em]">
                Мебель для центров управления
              </div>
            </div>
          </div>
        </div>

        {/* Subcategories grid */}
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.2em] text-[#FF6B00] font-semibold mb-2">
            Номенклатура направления
          </div>
          <h3 className="font-display text-[#0A192F] text-2xl lg:text-3xl font-semibold">
            Подкатегории поставок
          </h3>
        </div>

        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
          data-testid="furniture-subcategories"
        >
          {SUBCATEGORIES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.article
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.45, delay: (i % 3) * 0.05 }}
                data-testid={`furniture-sub-${i}`}
                className="group relative bg-white border border-slate-200 hover:border-[#0A192F] p-6 lg:p-7 flex flex-col transition-colors"
              >
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 bg-[#F1F5F9] group-hover:bg-[#0A192F] flex items-center justify-center transition-colors">
                    <Icon className="w-5 h-5 text-[#0A192F] group-hover:text-white transition-colors" />
                  </div>
                  <span className="text-xs tabular-nums font-semibold tracking-widest text-slate-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h4 className="font-display text-[#0A192F] text-lg font-semibold leading-snug">
                  {s.title}
                </h4>
                <p className="mt-3 text-slate-600 text-sm leading-relaxed flex-1">
                  {s.desc}
                </p>
                <a
                  href={mailto(`Запрос: ${s.title.toLowerCase()}`)}
                  data-testid={`furniture-sub-cta-${i}`}
                  className="mt-6 inline-flex items-center gap-1.5 text-[#0A192F] text-sm font-medium hover:text-[#FF6B00] transition-colors self-start"
                >
                  Запросить предложение
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              </motion.article>
            );
          })}
        </div>

        {/* Solutions block */}
        <div className="mt-20 lg:mt-28 bg-[#F8FAFC] border border-slate-200 p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5">
              <div className="eyebrow mb-6">Решения</div>
              <h3 className="font-display text-[#0A192F] text-2xl lg:text-3xl font-semibold leading-tight">
                Решения под задачи предприятия
              </h3>
              <p className="mt-5 text-slate-600 text-base leading-relaxed">
                Подбор промышленной мебели и комплектующих осуществляется с учётом
                назначения помещения, количества рабочих мест, размещаемого оборудования,
                требований к эргономике и организации кабелей.
              </p>
              <button
                onClick={() => scrollToId("request")}
                data-testid="furniture-solutions-cta"
                className="mt-8 inline-flex items-center gap-2 border border-[#0A192F] hover:bg-[#0A192F] hover:text-white text-[#0A192F] px-6 py-3.5 font-medium transition-colors"
              >
                Обсудить задачу
                <ArrowUpRight className="w-4 h-4" />
              </button>
            </div>
            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
              {FEATURES.map((f, i) => {
                const Icon = f.icon;
                return (
                  <motion.div
                    key={f.title}
                    initial={{ opacity: 0, y: 16 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-40px" }}
                    transition={{ duration: 0.45, delay: i * 0.06 }}
                    className="bg-white border border-slate-200 p-6"
                    data-testid={`furniture-feature-${i}`}
                  >
                    <Icon className="w-6 h-6 text-[#FF6B00] mb-4" />
                    <div className="font-display text-[#0A192F] font-semibold text-base leading-snug">
                      {f.title}
                    </div>
                    <div className="mt-2 text-sm text-slate-600 leading-relaxed">
                      {f.desc}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
